import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { clientesService } from '../../services/clientesService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';

function ClienteForm({ cliente, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    empresaID: '',
    nombreRazonSocial: '',
    rfc: '',
    usoCFDI: '',
    correoElectronico: '',
    domicilioFiscal: '',
    codigoPostal: '',
    regimenFiscal: '',
    telefono: '',
    activo: true,
    todasLasSucursales: true,
    sucursalID: null,
  });

  const [empresas, setEmpresas] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (formData.empresaID) {
      loadSucursales();
    } else {
      setSucursales([]);
    }
  }, [formData.empresaID]);

  useEffect(() => {
    if (cliente) {
      const sucursalId = cliente.SucursalID || cliente.sucursalID;
      setFormData({
        empresaID: cliente.EmpresaID || cliente.empresaID || '',
        nombreRazonSocial: cliente.NombreRazonSocial || cliente.nombreRazonSocial || '',
        rfc: cliente.RFC || cliente.rfc || '',
        usoCFDI: cliente.UsoCFDI || cliente.usoCFDI || '',
        correoElectronico: cliente.CorreoElectronico || cliente.correoElectronico || '',
        domicilioFiscal: cliente.DomicilioFiscal || cliente.domicilioFiscal || '',
        codigoPostal: cliente.CodigoPostal || cliente.codigoPostal || '',
        regimenFiscal: cliente.RegimenFiscal || cliente.regimenFiscal || '',
        telefono: cliente.Telefono || cliente.telefono || '',
        activo: cliente.Activo !== undefined ? cliente.Activo : (cliente.activo !== undefined ? cliente.activo : true),
        todasLasSucursales: !sucursalId,
        sucursalID: sucursalId || null,
      });
    }
  }, [cliente]);

  const loadEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const normalized = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
        }));
        setEmpresas(normalized);
        // Si solo hay una empresa, seleccionarla automáticamente
        if (normalized.length === 1 && !cliente) {
          setFormData(prev => ({ ...prev, empresaID: normalized[0].EmpresaID.toString() }));
        }
      }
    } catch (err) {
      setError('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresaId = parseInt(formData.empresaID);
      if (!empresaId) return;
      
      const response = await sucursalesService.getAll(empresaId);
      if (response.success && response.data) {
        const sucursalesData = Array.isArray(response.data) ? response.data : [];
        const normalized = sucursalesData.map(suc => ({
          SucursalID: suc.SucursalID || suc.sucursalID,
          Nombre: suc.Nombre || suc.nombre || '',
        }));
        setSucursales(normalized);
      }
    } catch (err) {
    } finally {
      setLoadingSucursales(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // EmpresaID es requerido
    if (!formData.empresaID) {
      newErrors.empresaID = 'La empresa es requerida';
    }

    // NombreRazonSocial es requerido
    if (!formData.nombreRazonSocial.trim()) {
      newErrors.nombreRazonSocial = 'El nombre o razón social es requerido';
    } else if (formData.nombreRazonSocial.length > 200) {
      newErrors.nombreRazonSocial = 'El nombre no puede exceder 200 caracteres';
    }

    // RFC opcional pero con validación
    if (formData.rfc) {
      if (formData.rfc.length < 12 || formData.rfc.length > 13) {
        newErrors.rfc = 'El RFC debe tener entre 12 y 13 caracteres';
      } else if (!/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(formData.rfc.toUpperCase())) {
        newErrors.rfc = 'El RFC no tiene un formato válido';
      }
    }

    // UsoCFDI opcional pero con límite
    if (formData.usoCFDI && formData.usoCFDI.length > 10) {
      newErrors.usoCFDI = 'El uso de CFDI no puede exceder 10 caracteres';
    }

    // CorreoElectronico opcional pero debe ser válido
    if (formData.correoElectronico) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.correoElectronico)) {
        newErrors.correoElectronico = 'El correo electrónico no es válido';
      } else if (formData.correoElectronico.length > 100) {
        newErrors.correoElectronico = 'El correo no puede exceder 100 caracteres';
      }
    }

    // DomicilioFiscal opcional pero con límite
    if (formData.domicilioFiscal && formData.domicilioFiscal.length > 500) {
      newErrors.domicilioFiscal = 'El domicilio fiscal no puede exceder 500 caracteres';
    }

    // CodigoPostal opcional pero debe ser válido (5 dígitos)
    if (formData.codigoPostal) {
      if (!/^\d{5}$/.test(formData.codigoPostal.trim())) {
        newErrors.codigoPostal = 'El código postal debe tener 5 dígitos';
      } else if (formData.codigoPostal.length > 10) {
        newErrors.codigoPostal = 'El código postal no puede exceder 10 caracteres';
      }
    }

    // RegimenFiscal opcional pero con límite
    if (formData.regimenFiscal && formData.regimenFiscal.length > 10) {
      newErrors.regimenFiscal = 'El régimen fiscal no puede exceder 10 caracteres';
    }

    // Telefono opcional pero con límite
    if (formData.telefono && formData.telefono.length > 20) {
      newErrors.telefono = 'El teléfono no puede exceder 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const clienteData = {
        EmpresaID: parseInt(formData.empresaID),
        SucursalID: formData.todasLasSucursales ? null : (formData.sucursalID || null),
        NombreRazonSocial: formData.nombreRazonSocial.trim(),
        RFC: formData.rfc?.trim().toUpperCase() || null,
        UsoCFDI: formData.usoCFDI?.trim() || null,
        CorreoElectronico: formData.correoElectronico?.trim() || null,
        DomicilioFiscal: formData.domicilioFiscal?.trim() || null,
        CodigoPostal: formData.codigoPostal?.trim() || null,
        RegimenFiscal: formData.regimenFiscal?.trim() || null,
        Telefono: formData.telefono?.trim() || null,
      };

      let response;
      if (cliente) {
        // Actualizar
        const clienteId = cliente.ClienteID || cliente.clienteID;
        clienteData.ClienteID = clienteId;
        clienteData.Activo = formData.activo;
        response = await clientesService.update(clienteId, clienteData);
      } else {
        // Crear
        response = await clientesService.create(clienteData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al guardar el cliente');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Empresa <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="empresaID"
              value={formData.empresaID}
              onChange={handleChange}
              invalid={!!errors.empresaID}
              disabled={loading || loadingEmpresas || !!cliente || empresas.length <= 1}
              required
            >
              <option value="">Seleccione una empresa</option>
              {empresas.map((emp) => (
                <option key={emp.EmpresaID} value={emp.EmpresaID}>
                  {emp.RazonSocial}
                </option>
              ))}
            </Input>
            {errors.empresaID && (
              <div className="text-danger small">{errors.empresaID}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Sucursales</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="todasLasSucursales"
                  checked={formData.todasLasSucursales}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      todasLasSucursales: e.target.checked,
                      sucursalID: e.target.checked ? null : prev.sucursalID,
                    }));
                  }}
                  disabled={!formData.empresaID || loadingSucursales}
                />
                <span className="form-check-sign">
                  <span className="check" />
                </span>
                Todas las sucursales
              </Label>
            </FormGroup>
            {!formData.todasLasSucursales && (
              <Input
                type="select"
                value={formData.sucursalID || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    sucursalID: e.target.value ? parseInt(e.target.value) : null,
                  }));
                }}
                disabled={!formData.empresaID || loadingSucursales}
              >
                <option value="">Seleccione una sucursal</option>
                {sucursales.map((suc) => (
                  <option key={suc.SucursalID} value={suc.SucursalID}>
                    {suc.Nombre}
                  </option>
                ))}
              </Input>
            )}
            {formData.empresaID && sucursales.length === 0 && !loadingSucursales && (
              <small className="form-text text-muted">
                No hay sucursales disponibles para esta empresa.
              </small>
            )}
            {loadingSucursales && (
              <small className="form-text text-muted">
                Cargando sucursales...
              </small>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Nombre o Razón Social <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="nombreRazonSocial"
              value={formData.nombreRazonSocial}
              onChange={handleChange}
              invalid={!!errors.nombreRazonSocial}
              maxLength={200}
              required
            />
            {errors.nombreRazonSocial && (
              <div className="text-danger small">{errors.nombreRazonSocial}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>RFC</Label>
            <Input
              type="text"
              name="rfc"
              value={formData.rfc}
              onChange={handleChange}
              invalid={!!errors.rfc}
              maxLength={13}
              placeholder="Ej: ABC123456XYZ"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.rfc && (
              <div className="text-danger small">{errors.rfc}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Uso CFDI</Label>
            <Input
              type="text"
              name="usoCFDI"
              value={formData.usoCFDI}
              onChange={handleChange}
              invalid={!!errors.usoCFDI}
              maxLength={10}
              placeholder="Ej: G01, G03"
            />
            {errors.usoCFDI && (
              <div className="text-danger small">{errors.usoCFDI}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Correo Electrónico</Label>
            <Input
              type="email"
              name="correoElectronico"
              value={formData.correoElectronico}
              onChange={handleChange}
              invalid={!!errors.correoElectronico}
              maxLength={100}
            />
            {errors.correoElectronico && (
              <div className="text-danger small">{errors.correoElectronico}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Teléfono</Label>
            <Input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              invalid={!!errors.telefono}
              maxLength={20}
            />
            {errors.telefono && (
              <div className="text-danger small">{errors.telefono}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Domicilio Fiscal</Label>
        <Input
          type="textarea"
          name="domicilioFiscal"
          value={formData.domicilioFiscal}
          onChange={handleChange}
          invalid={!!errors.domicilioFiscal}
          maxLength={500}
          rows={3}
        />
        {errors.domicilioFiscal && (
          <div className="text-danger small">{errors.domicilioFiscal}</div>
        )}
      </FormGroup>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Código Postal</Label>
            <Input
              type="text"
              name="codigoPostal"
              value={formData.codigoPostal}
              onChange={handleChange}
              invalid={!!errors.codigoPostal}
              maxLength={10}
              placeholder="Ej: 01000"
              pattern="[0-9]{5}"
            />
            {errors.codigoPostal && (
              <div className="text-danger small">{errors.codigoPostal}</div>
            )}
            <small className="form-text text-muted">
              Código postal de 5 dígitos (requerido para facturación)
            </small>
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Régimen Fiscal</Label>
            <Input
              type="text"
              name="regimenFiscal"
              value={formData.regimenFiscal}
              onChange={handleChange}
              invalid={!!errors.regimenFiscal}
              maxLength={10}
              placeholder="Ej: 616"
            />
            {errors.regimenFiscal && (
              <div className="text-danger small">{errors.regimenFiscal}</div>
            )}
            <small className="form-text text-muted">
              Código del régimen fiscal según el SAT (ej: 616 = General de Ley Personas Morales)
            </small>
          </FormGroup>
        </Col>
      </Row>

      {cliente && (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
            Cliente Activo
          </Label>
        </FormGroup>
      )}

      <div className="text-right mt-4">
        <Button color="secondary" onClick={onCancel} className="mr-2">
          Cancelar
        </Button>
        <Button color="primary" type="submit" disabled={loading || loadingEmpresas}>
          {loading ? 'Guardando...' : cliente ? 'Actualizar' : 'Crear'}
        </Button>
      </div>
    </Form>
  );
}

export default ClienteForm;

